/******************************************************************
 * USA CARRIER DATA — Informational guidance notes only.
 * These do NOT affect calculation logic.
 ******************************************************************/

export type USACarrier =
  | "penn_mutual"
  | "lafayette_life"
  | "mutual_trust"
  | "oneamerica"
  | "generic";

export const usaCarrierLabels: Record<USACarrier, string> = {
  penn_mutual: "Penn Mutual",
  lafayette_life: "Lafayette Life",
  mutual_trust: "Mutual Trust",
  oneamerica: "OneAmerica",
  generic: "Generic USA Whole Life",
};

export const usaCarrierNotes: Record<USACarrier, string> = {
  penn_mutual:
    "Penn Mutual participating whole life policies commonly use direct recognition for dividend treatment. This planner does not model dividend performance. Enter the current policy loan rate for planning purposes.",
  lafayette_life:
    "Lafayette Life participating whole life policies are commonly described as non-direct recognition in advisor materials. This planner does not calculate dividend performance. Enter the current policy loan rate for repayment planning.",
  mutual_trust:
    "Mutual Trust participating whole life policies may administer policy loans differently depending on premium billing mode. Interest may be calculated in advance and aligned with policy anniversary or billing cycles.",
  oneamerica:
    "OneAmerica participating whole life policies may vary by policy series. Enter the current policy loan rate for planning purposes.",
  generic:
    "Use this option when a carrier is not listed. Confirm policy loan behavior directly with the carrier or policy documents.",
};

export type PaymentFrequency = "weekly" | "biweekly" | "semimonthly" | "monthly" | "quarterly" | "semiannual" | "annual" | "endofterm";

export function getCarrierFrequencyNote(carrier: USACarrier, frequency: PaymentFrequency): string | null {
  const baseNote = "Repayment frequency in this planner represents borrower repayment discipline. Actual carrier loan administration may follow policy anniversary timing or billing cycles.";

  if (carrier === "mutual_trust") {
    if (frequency === "monthly" || frequency === "annual") {
      return `${baseNote}\n\nMutual Trust loan administration may align with the policy anniversary when premiums are monthly or annual.`;
    }
    if (frequency === "quarterly" || frequency === "semiannual") {
      return `${baseNote}\n\nMutual Trust loan administration may align with billing cycles when premiums are quarterly or semi-annual.`;
    }
  }

  return baseNote;
}

export const directRecognitionExplainer = {
  title: "What is Direct Recognition?",
  content:
    "Direct recognition means a life insurance company may adjust dividends on policy values that are supporting a loan. Non-direct recognition means the company generally does not distinguish loaned values when crediting dividends. This planner does not calculate dividend performance, so recognition type is provided for educational context only.",
};
